import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ConfirmationService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { filter, finalize } from 'rxjs';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LiveUpdateEventTypes, notificationMessagesTopic } from '../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { AinCanAccessDirective } from '../../shared/security/access.directive';
import { NotificationMessageDetailExtended } from './notification.models';
import { NotificationService } from './notification.service';
import { canCancelMessage, canResendMessage, maskDestinationHash, normalizeNotificationChannel } from './notification.utils';

@Component({
  standalone: true,
  selector: 'ain-notifications-message-detail',
  providers: [ConfirmationService],
  imports: [
    RouterLink, AinPageComponent, AinStateComponent, InfoHintComponent, ButtonModule,
    TableModule, TabsModule, ConfirmDialogModule, AinDatePipe, AinCanAccessDirective
  ],
  template: `
    <ain-page [eyebrow]="t('notifications.eyebrow')" [title]="t('notifications.messageDetail.title')" [description]="t('notifications.messageDetail.description')">
      <div class="toolbar">
        <a routerLink="/notifications/messages" pButton severity="secondary" [label]="t('common.back')"></a>
        @if (detail()) {
          @if (canCancel(detail()!.message.status)) {
            <button ainCanAccess [ainCanAccessPermission]="'notifications.send.execute'" pButton type="button" severity="warn" [label]="t('notifications.messageDetail.actions.cancel')" (click)="confirmCancel()"></button>
          }
          @if (canResend(detail()!.message.status)) {
            <button ainCanAccess [ainCanAccessPermission]="'notifications.send.execute'" pButton type="button" [label]="t('notifications.messageDetail.actions.resend')" (click)="confirmResend()"></button>
          }
        }
      </div>

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('common.error')" [message]="error() ?? ''" />
      } @else if (!detail()) {
        <ain-state type="empty" [title]="t('common.notFound')" />
      } @else {
        <section class="card summary">
          <ain-info-hint [titleKey]="'notifications.messageDetail.help.overview.title'" [descriptionKey]="'notifications.messageDetail.help.overview.description'" />
          <div class="grid">
            <div><span class="label">{{ t('notifications.messages.columns.subject') }}</span><div>{{ detail()!.message.subject }}</div></div>
            <div><span class="label">{{ t('notifications.messages.columns.channel') }}</span><div>{{ channelLabel(detail()!.message.channel) }}</div></div>
            <div><span class="label">{{ t('notifications.messages.columns.status') }}</span><div>{{ detail()!.message.status }}</div></div>
            <div><span class="label">{{ t('notifications.messageDetail.fields.messageKey') }}</span><div>{{ detail()!.message.messageKey }}</div></div>
            <div><span class="label">{{ t('notifications.messages.columns.createdAt') }}</span><div>{{ detail()!.message.createdAtUtc | ainDate:'datetime' }}</div></div>
          </div>
        </section>

        <p-tabs value="recipients">
          <p-tablist>
            <p-tab value="recipients">{{ t('notifications.messageDetail.tabs.recipients') }}</p-tab>
            <p-tab value="attempts">{{ t('notifications.messageDetail.tabs.attempts') }}</p-tab>
            <p-tab value="content">{{ t('notifications.messageDetail.tabs.content') }}</p-tab>
            <p-tab value="events">{{ t('notifications.messageDetail.tabs.events') }}</p-tab>
          </p-tablist>
          <p-tabpanels>
            <p-tabpanel value="recipients">
              <section class="card">
                <p-table [value]="detail()!.message.recipients" [paginator]="true" [rows]="10">
                  <ng-template pTemplate="header">
                    <tr>
                      <th>{{ t('notifications.messageDetail.columns.party') }}</th>
                      <th>{{ t('notifications.messageDetail.columns.destination') }}</th>
                      <th>{{ t('notifications.messageDetail.columns.status') }}</th>
                      <th>{{ t('notifications.messageDetail.columns.provider') }}</th>
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="body" let-row>
                    <tr>
                      <td>{{ row.partyDisplayName }}</td>
                      <td>{{ maskHash(row.destinationHash) }}</td>
                      <td>{{ row.status }}</td>
                      <td>{{ row.providerKey || '—' }}</td>
                    </tr>
                  </ng-template>
                </p-table>
              </section>
            </p-tabpanel>
            <p-tabpanel value="attempts">
              <section class="card">
                <p-table [value]="detail()!.message.deliveryAttempts" [paginator]="true" [rows]="10">
                  <ng-template pTemplate="header">
                    <tr><th>{{ t('notifications.messageDetail.columns.provider') }}</th><th>{{ t('notifications.messageDetail.columns.attempt') }}</th><th>{{ t('notifications.messageDetail.columns.status') }}</th><th>{{ t('notifications.messageDetail.columns.startedAt') }}</th></tr>
                  </ng-template>
                  <ng-template pTemplate="body" let-row>
                    <tr>
                      <td>{{ row.providerKey }}</td>
                      <td>{{ row.attemptNumber }}</td>
                      <td>{{ row.status }}</td>
                      <td>{{ row.startedAtUtc | ainDate:'datetime' }}</td>
                    </tr>
                  </ng-template>
                </p-table>
              </section>
            </p-tabpanel>
            <p-tabpanel value="content">
              <section class="card content-panel" [class.rtl]="isPersianLocale()">
                <h4>{{ t('notifications.messageDetail.sections.renderedSubject') }}</h4>
                <p>{{ detail()!.message.subject }}</p>
                <h4>{{ t('notifications.messageDetail.sections.renderedBody') }}</h4>
                <div class="body-preview" [innerHTML]="detail()!.message.body"></div>
              </section>
            </p-tabpanel>
            <p-tabpanel value="events">
              <section class="card">
                <p-table [value]="detail()!.events" [paginator]="true" [rows]="10">
                  <ng-template pTemplate="header"><tr><th>{{ t('notifications.messageDetail.columns.eventType') }}</th><th>{{ t('notifications.messageDetail.columns.occurredAt') }}</th></tr></ng-template>
                  <ng-template pTemplate="body" let-row>
                    <tr><td>{{ row.eventType }}</td><td>{{ row.occurredAtUtc | ainDate:'datetime' }}</td></tr>
                  </ng-template>
                </p-table>
              </section>
            </p-tabpanel>
          </p-tabpanels>
        </p-tabs>
      }
      <p-confirmDialog />
    </ain-page>
  `,
  styles: [`
    .toolbar { display:flex; gap:8px; flex-wrap:wrap; margin-bottom:12px; }
    .card { border:1px solid #e5e7eb; border-radius:12px; padding:12px; margin-bottom:12px; }
    .grid { display:grid; gap:10px; grid-template-columns:repeat(auto-fit,minmax(180px,1fr)); margin-top:10px; }
    .label { color:#6b7280; font-size:0.85rem; display:block; }
    .body-preview { border:1px solid #e5e7eb; border-radius:8px; padding:12px; background:#fafafa; }
    .content-panel.rtl { direction:rtl; text-align:right; }
  `]
})
export class NotificationsMessageDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly api = inject(NotificationService);
  private readonly i18n = inject(LocalizationService);
  private readonly confirmation = inject(ConfirmationService);
  private readonly context = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly liveUpdateService = inject(LiveUpdateService);

  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly detail = signal<NotificationMessageDetailExtended | null>(null);
  readonly canCancel = canCancelMessage;
  readonly canResend = canResendMessage;
  readonly maskHash = maskDestinationHash;

  ngOnInit(): void {
    this.liveUpdateService.events$
      .pipe(
        filter(event => event.eventType === LiveUpdateEventTypes.notificationMessagesChanged),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.load(false));

    void this.setupLiveSubscription();
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      const id = params.get('messageId');
      if (id) this.load(true, id);
    });
  }

  t(key: string): string { return this.i18n.translate(key); }

  channelLabel(channel: unknown): string {
    return this.t(`notifications.enums.channel.${normalizeNotificationChannel(channel as never)}`);
  }

  isPersianLocale(): boolean {
    return (this.i18n.activeCulture() ?? '').startsWith('fa');
  }

  confirmCancel(): void {
    if (!this.canExecuteSend()) return;
    const id = this.detail()?.message.id;
    if (!id) return;
    this.confirmation.confirm({
      header: this.t('notifications.messageDetail.actions.cancel'),
      message: this.t('notifications.messageDetail.confirm.cancel'),
      accept: () => this.api.cancelMessage(id).subscribe(() => this.load(false, id))
    });
  }

  confirmResend(): void {
    if (!this.canExecuteSend()) return;
    const id = this.detail()?.message.id;
    if (!id) return;
    this.confirmation.confirm({
      header: this.t('notifications.messageDetail.actions.resend'),
      message: this.t('notifications.messageDetail.confirm.resend'),
      accept: () => this.api.resendMessage(id).subscribe(() => this.load(false, id))
    });
  }

  private load(showLoading: boolean, messageId = this.route.snapshot.paramMap.get('messageId') ?? ''): void {
    if (!messageId) return;
    if (showLoading) this.loading.set(true);
    this.api.getMessage(messageId).pipe(finalize(() => this.loading.set(false))).subscribe({
      next: detail => this.detail.set(detail),
      error: () => this.error.set(this.t('common.error'))
    });
  }

  private async setupLiveSubscription(): Promise<void> {
    const tenantId = this.context.context()?.tenantId;
    if (!tenantId) return;
    await this.liveUpdateService.subscribe(notificationMessagesTopic(tenantId));
  }

  private canExecuteSend(): boolean {
    const context = this.context.context();
    return (context?.roles ?? []).includes('SystemAdmin') ||
      (context?.permissions ?? []).some(item => item.key === 'notifications.send.execute');
  }
}
